import React from 'react';
import { Box, Typography, LinearProgress, Paper, Card, Grid, Divider, Avatar, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Chart from 'react-apexcharts';

const SummaryCard = ({ title, value, icon: Icon, color }) => (
  <Card
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'transform 0.3s',
      '&:hover': { transform: 'scale(1.01)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Avatar sx={{ bgcolor: color, mr: 2 }}>
        <Icon />
      </Avatar>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {title}
        </Typography>
        <Typography variant="h6">{value}</Typography>
      </Box>
    </Box>
    <Tooltip title={`Details about ${title}`}>
      <InfoIcon sx={{ color: 'primary.main' }} />
    </Tooltip>
  </Card>
);

const ProfileStrength = ({ profileStrengthData }) => {
  const { insights, skills, strengthPercentage } = profileStrengthData;

  const skillCategories = Object.entries(skills).map(([category, skillSet]) => ({
    category,
    skills: Object.entries(skillSet).map(([skill, proficiency]) => ({ skill, proficiency })),
  }));

  const radarData = [
    { category: 'Skills', value: insights.totalSkills },
    { category: 'Languages', value: insights.totalLanguages },
    { category: 'Work History', value: insights.totalWorkHistory },
    { category: 'Education', value: insights.totalEducation },
    { category: 'Projects', value: insights.totalProjects },
  ];

  const barData = radarData.map((item) => ({ x: item.category, y: item.value }));

  const radarChartOptions = {
    chart: {
      type: 'radar',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      categories: radarData.map((data) => data.category),
      labels: {
        style: {
          colors: '#6e6e6e',
          fontSize: '14px',
        },
      },
    },
    plotOptions: {
      radar: {
        polygons: {
          strokeColors: '#e9e9e9',
          connectorColors: '#e9e9e9',
        },
      },
    },
    fill: {
      opacity: 0.4,
    },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const category = w.globals.categoryLabels[dataPointIndex];
        const skillsInCategory = skillCategories.find((cat) => cat.category === category)?.skills || [];
        return `
          <div style="padding: 10px; position: absolute; bottom: -10px; left: 0; z-index: 1000; background: #fff; border: 1px solid #ccc; box-shadow: 0 2px 5px rgba(0,0,0,0.1); border-radius: 5px;">
          <h4 style="margin: 0; padding: 0; font-size: 14px;">${category}</h4>
          <ul style="list-style: none; margin: 5px 0 0 0; padding: 0;">
            ${skillsInCategory.map((skill) => `<li>${skill.skill}: ${skill.proficiency}</li>`).join('')}
          </ul>
        </div>
        `;
      },
    },
  };

  const radarChartSeries = [
    {
      name: 'Profile Strength',
      data: radarData.map((data) => data.value),
    },
  ];

  const pieData = skillCategories.map((category) => ({
    name: category.category,
    value: category.skills.length,
  }));

  const pieChartOptions = {
    chart: {
      type: 'pie',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: true,
      },
    },
    labels: pieData.map((data) => data.name),
    colors: ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AA00FF', '#FF4081'],
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
    },
    tooltip: {
      custom: ({ series, seriesIndex, w }) => {
        const category = w.globals.labels[seriesIndex];
        const skillsInCategory = skillCategories.find((cat) => cat.category === category)?.skills || [];
        return `
          <div style="padding: 10px;">
            <h4>${category}</h4>
            <ul>
              ${skillsInCategory.map((skill) => `<li>${skill.skill}: ${skill.proficiency}</li>`).join('')}
            </ul>
          </div>
        `;
      },
    },
  };

  const pieChartSeries = pieData.map((data) => data.value);

  const barChartOptions = {
    chart: {
      type: 'bar',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
        },
      },
    },
    xaxis: {
      categories: barData.map((data) => data.x),
      labels: {
        style: {
          colors: '#6e6e6e',
          fontSize: '14px',
        },
      },
    },
    fill: {
      colors: ['#008FFB'],
    },
  };

  const barChartSeries = [
    {
      name: 'Value',
      data: barData.map((data) => data.y),
    },
  ];

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, width: '100%' }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main' }}
      >
        <AssessmentIcon sx={{ mr: 1 }} /> Profile Strength
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <LinearProgress
          variant="determinate"
          value={strengthPercentage}
          sx={{
            width: '100%',
            mr: 2,
            height: 10,
            borderRadius: 5,
            '& .MuiLinearProgress-bar': {
              backgroundColor: strengthPercentage > 80 ? '#4caf50' : strengthPercentage > 50 ? '#ff9800' : '#f44336',
            },
          }}
        />
        <Typography
          variant="h6"
          sx={{
            minWidth: 50,
            textAlign: 'right',
            color: strengthPercentage > 80 ? '#4caf50' : strengthPercentage > 50 ? '#ff9800' : '#f44336',
          }}
        >
          {strengthPercentage}%
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
        <BarChartIcon sx={{ mr: 1 }} /> Insights
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={2}>
        {Object.entries(insights)?.map(([key, value]) => (
          <>
            {typeof value !== 'string' ? null : (
              <Grid item xs={12} key={key}>
                <SummaryCard title={key} value={value} icon={InfoIcon} color="primary.main" />
              </Grid>
            )}
          </>
        ))}
      </Grid>
      {skills.length > 0 ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
            <PieChartIcon sx={{ mr: 1 }} /> Skills Composition
          </Typography>
          <Chart options={pieChartOptions} series={pieChartSeries} type="pie" height={350} />
        </Box>
      ) : null}
    </Paper>
  );
};

export default ProfileStrength;
